import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Heart, Users, BookOpen, ArrowRight, Clock, Youtube } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { heroSlides } from '../mock';
import { useSiteInfo } from '../context/SiteSettingsContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Extract a YouTube video id from any of the common URL shapes we accept.
const extractYouTubeId = (url) => {
  if (!url || typeof url !== 'string') return '';
  const patterns = [
    /youtube\.com\/embed\/([A-Za-z0-9_-]{6,})/,
    /youtube\.com\/watch\?v=([A-Za-z0-9_-]{6,})/,
    /youtu\.be\/([A-Za-z0-9_-]{6,})/,
    /youtube\.com\/shorts\/([A-Za-z0-9_-]{6,})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m && m[1]) return m[1];
  }
  return '';
};

const Home = () => {
  const churchInfo = useSiteInfo();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const [latestVideoId, setLatestVideoId] = useState('');
  const [latestVideoTitle, setLatestVideoTitle] = useState('');

  // Prefer the latest video from the church's YouTube channel (auto-updates),
  // and fall back to the manually-set embed URL from site settings.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/youtube/latest?limit=1`);
        if (!cancelled && data?.latest?.id) {
          setLatestVideoId(data.latest.id);
          setLatestVideoTitle(data.latest.title || '');
        }
      } catch (e) {
        // Silent fallback — Home will use the manual site-settings video instead.
        console.warn('[home] YouTube latest fetch failed', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const homeVideoId = useMemo(
    () => latestVideoId || extractYouTubeId(churchInfo.youtubeEmbedUrl),
    [latestVideoId, churchInfo.youtubeEmbedUrl],
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);

  return (
    <div className="min-h-screen">
      {/* Hero Slider */}
      <section className="relative h-[480px] sm:h-[560px] md:h-[600px] overflow-hidden mt-20">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div
              className="absolute inset-0 z-10"
              style={{
                background:
                  slide.overlay ||
                  'linear-gradient(to bottom, rgba(0,0,0,0.75), rgba(0,0,0,0.65), rgba(0,0,0,0.8))',
              }}
            />
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
              loading={index === 0 ? 'eager' : 'lazy'}
              decoding="async"
              fetchPriority={index === 0 ? 'high' : 'low'}
              width="1920"
              height="1080"
            />
            <div className="absolute inset-0 z-20 flex items-center justify-center">
              <div className="text-center text-white max-w-4xl px-4">
                <h1
                  className="mb-6 animate-fade-in"
                  style={{
                    fontFamily: "'Anton', sans-serif",
                    fontWeight: 400,
                    fontSize: 'clamp(3rem, 13vw, 9.5rem)',
                    lineHeight: 1,
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em',
                  }}
                >
                  {slide.title}
                </h1>
                <p className="text-base sm:text-xl md:text-2xl text-slate-200 animate-fade-in">{slide.subtitle}</p>
              </div>
            </div>
          </div>
        ))}

        {/* Slider Controls */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300"
        >
          <ChevronRight className="h-6 w-6" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-2">
          {heroSlides.map((slide, index) => (
            <button
              key={slide.id}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-10 md:py-14 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-8 md:mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Welcome to Christ Methodist Church
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-600 mx-auto mb-8"></div>
          </div>

          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Welcome to the Christ Methodist Church. We're a family. From the moment you step in, you are more than a visitor; you belong.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Our worship is Christ-centred and heartfelt grounded in the Word, lifted in worship, and lived out in witness. Each service gives you strength for today and hope for tomorrow.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed mb-10">
              At CMC, love and care are guaranteed. You will find a community that walks with you, prays for you, and celebrates life together. Come as you are here, faith feels like family.
            </p>

            <div className="text-center">
              <Link to="/events">
                <Button size="lg" className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300">
                  Visit Us This Sunday
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Community Care Section */}
      <section className="py-10 md:py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-8 md:mb-12">
            <p className="text-2xl md:text-3xl text-slate-600 italic mb-8">
              "We're here for all who seek God's Word, Christ's love, and a caring community."
            </p>
            <h3 className="text-3xl font-bold text-slate-900 mb-4">Here's how we can help you</h3>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              At Christ Methodist Church, we care for the whole person heart, mind, and spirit. Whatever season of life you are in, we want to walk alongside you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <Card className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200">
              <CardContent className="p-8 text-center">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Heart className="h-10 w-10 text-blue-600" />
                </div>
                <h4 className="text-xl font-semibold text-slate-900 mb-4">Request Prayer</h4>
                <p className="text-slate-600 mb-6">
                  Share your prayer needs with our caring community. We believe in the power of prayer.
                </p>
                <Link to="/contact?type=prayer">
                  <Button variant="outline" className="group-hover:bg-blue-50 group-hover:border-blue-300">
                    Request Prayer
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200">
              <CardContent className="p-8 text-center">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Users className="h-10 w-10 text-blue-600" />
                </div>
                <h4 className="text-xl font-semibold text-slate-900 mb-4">Join a Group</h4>
                <p className="text-slate-600 mb-6">
                  Connect with others through our ministries, small groups, and fellowship opportunities.
                </p>
                <Link to="/ministries">
                  <Button variant="outline" className="group-hover:bg-blue-50 group-hover:border-blue-300">
                    Explore Ministries
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200">
              <CardContent className="p-8 text-center">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <BookOpen className="h-10 w-10 text-blue-600" />
                </div>
                <h4 className="text-xl font-semibold text-slate-900 mb-4">Talk to Us</h4>
                <p className="text-slate-600 mb-6">
                  Have questions? Want to know more? Our pastoral team is here to listen and help.
                </p>
                <Link to="/contact">
                  <Button variant="outline" className="group-hover:bg-blue-50 group-hover:border-blue-300">
                    Contact Us
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Watch & Grow — YouTube */}
      <section className="py-10 md:py-14 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <div className="inline-flex items-center gap-2 mb-5">
              <Youtube className="h-5 w-5 text-red-600" />
              <span className="text-slate-700 font-semibold tracking-wide uppercase text-sm">Watch &amp; Grow</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-3">
              Step into the Word with us.
            </h2>
          </div>

          <div className="relative max-w-4xl mx-auto">
            {homeVideoId ? (
              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                data-testid="home-youtube-poster"
                aria-label="Play the latest sermon video"
                className="group relative block w-full rounded-2xl overflow-hidden shadow-xl ring-1 ring-slate-200 hover:ring-blue-300 hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-300"
              >
                <div className="relative aspect-video bg-slate-900">
                  <img
                    src={`https://img.youtube.com/vi/${homeVideoId}/maxresdefault.jpg`}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      e.currentTarget.src = `https://img.youtube.com/vi/${homeVideoId}/hqdefault.jpg`;
                    }}
                  />
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/25 transition-colors duration-300" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-red-600/95 group-hover:bg-red-600 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300">
                      <svg viewBox="0 0 24 24" fill="white" className="w-9 h-9 md:w-11 md:h-11 ml-1">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent p-4 md:p-5">
                    <div className="flex items-center gap-2 text-white">
                      <Youtube className="h-4 w-4 md:h-5 md:w-5 text-red-500" />
                      <span className="text-sm md:text-base font-semibold line-clamp-2 text-left">
                        {latestVideoTitle || 'Watch our latest message'}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            ) : (
              <div
                className="relative w-full overflow-hidden rounded-2xl shadow-xl border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center text-center"
                style={{ paddingBottom: '56.25%' }}
                data-testid="home-youtube-placeholder"
              >
                <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                  <Youtube className="h-16 w-16 text-red-500 mb-3" />
                  <p className="text-white text-lg font-medium mb-1">YouTube channel coming soon</p>
                  <p className="text-slate-300 text-sm">Stay tuned for sermons and worship videos.</p>
                </div>
              </div>
            )}
          </div>

          {/* In-page YouTube modal player */}
          <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
            <DialogContent
              className="p-0 border-0 bg-black max-w-4xl w-[95vw] overflow-hidden"
              data-testid="home-youtube-modal"
            >
              <DialogTitle className="sr-only">Christ Methodist Church video</DialogTitle>
              <DialogDescription className="sr-only">
                Watch the latest sermon or worship video from Christ Methodist Church, Kothanur.
              </DialogDescription>
              <div className="aspect-video">
                {videoOpen && homeVideoId && (
                  <iframe
                    src={`https://www.youtube.com/embed/${homeVideoId}?autoplay=1&rel=0`}
                    title="Christ Methodist Church — Video"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    data-testid="home-youtube-iframe"
                  />
                )}
              </div>
            </DialogContent>
          </Dialog>

          {churchInfo.youtubeChannelUrl && (
            <div className="mt-6 text-center">
              <a
                href={churchInfo.youtubeChannelUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-youtube-channel-link"
                className="inline-flex items-center gap-2 bg-white px-5 py-2 rounded-full shadow-md hover:shadow-lg transition-shadow"
              >
                <Youtube className="h-5 w-5 text-red-600" />
                <span className="text-slate-800 font-semibold">Visit Our Channel</span>
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Location & Invitation */}
      <section className="py-10 md:py-14 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-8 md:mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Come and See Join Us This Sunday
            </h2>
            <p className="text-lg text-slate-600">We can't wait to welcome you into our family</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            <Card className="p-8 flex flex-col">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Service Times</h3>
              <div className="space-y-4 flex-1">
                {churchInfo.serviceTimes.map((service) => (
                  <div key={`${service.day}-${service.time}`} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                    <Clock className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-slate-900">{service.day} - {service.time}</p>
                      <p className="text-slate-600">{service.type}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-8 flex flex-col" data-testid="home-find-us-card">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Find Us</h3>
              <div className="flex flex-col flex-1 gap-4">
                <div className="rounded-lg overflow-hidden border border-slate-200 flex-1 min-h-[260px]">
                  <iframe
                    src={churchInfo.mapEmbed}
                    title="Christ Methodist Church, Kothanur — Map"
                    className="w-full h-full"
                    style={{ border: 0, minHeight: '260px' }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                    data-testid="home-find-us-map"
                  />
                </div>
                <a
                  href={churchInfo.mapLocation}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                  data-testid="home-find-us-directions-link"
                >
                  <Button className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700">
                    Get Directions
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
